import { ICON_REGISTRY, type IconName } from './lucide.ts';

/**
 * The Cavaquinho Icon API — the layer everything else in the application draws
 * icons through.
 *
 * Design system § 10 fixes the style — rounded outline, 2px stroke on a 24
 * grid, round caps, no fills — which is what Lucide already draws by default,
 * so compliance is not a per-icon override (research D-022). What this layer
 * owns is what the library does not know: the stroke scales with the size, and
 * an icon is either decoration or a name, never accidentally both.
 *
 * A caller passes a NAME, not a glyph (research D-030). That is what keeps the
 * icon set closed and reviewable, keeps these rules impossible to override at a
 * call site, and keeps `lucide-react` reachable from exactly one file.
 */

/** § 10 sizes: 16 inline, 20 label, 24 default, 32 in a container. */
export type IconSize = 16 | 20 | 24 | 32;

/** § 10: stroke scales with size — 1.75 at 16, 2.25 at 32. */
const STROKE_BY_SIZE: Record<IconSize, number> = {
  16: 1.75,
  20: 2,
  24: 2,
  32: 2.25,
};

interface IconBaseProps {
  /** The icon to draw, named from the curated registry. */
  name: IconName;
  size?: IconSize;
}

interface DecorativeIconProps extends IconBaseProps {
  /**
   * Decorative icons are hidden from assistive technology. Design system § 12
   * requires that no state be carried by an icon alone, so an icon beside a
   * label is decoration and must not be announced twice.
   */
  label?: undefined;
}

interface LabelledIconProps extends IconBaseProps {
  /**
   * The accessible name, for an icon that is the only carrier of its meaning.
   * If a visible text label already says the same thing, omit this.
   */
  label: string;
}

export type IconProps = DecorativeIconProps | LabelledIconProps;

export function Icon({ name, size = 24, label }: IconProps) {
  const Glyph = ICON_REGISTRY[name];
  const decorative = label === undefined;

  return (
    <Glyph
      size={size}
      /*
       * Not `absoluteStrokeWidth`: that keeps the rendered stroke visually
       * constant across sizes by dividing it back out, which is the opposite of
       * what § 10 asks. The stroke is meant to grow with the glyph.
       */
      strokeWidth={STROKE_BY_SIZE[size]}
      aria-hidden={decorative || undefined}
      aria-label={label}
      role={decorative ? undefined : 'img'}
      focusable="false"
    />
  );
}
